import { useSuspenseQuery } from "@apollo/client";
import {
  DataTable,
  DataTableColumnHeader,
  DateUI,
  PrimaryFullScreenSpinner,
  toast,
} from "@repo/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Suspense, useEffect } from "react";
import { BusinessEntityColumnCell } from "~/components/tables/BusinessEntityColumnCell";
import { graphql } from "~/gql/client";
import type { AdminCurrencycloudAccountFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/currencycloud/")({
  head: () => opsHead("Currencycloud Accounts"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { data, error } = useSuspenseQuery(
    graphql(`
      query AdminCurrencycloudAccounts {
        currencycloudAccounts {
          ...AdminCurrencycloudAccount
        }
      }

      fragment AdminCurrencycloudAccount on CurrencycloudAccount {
        id
        accountName
        status
        country
        legalEntityType
        createdAt
        updatedAt
        businessEntity {
          id
          name
          organisation {
            id
            name
          }
        }
      }
    `),
  );
  const accounts = data.currencycloudAccounts ?? [];

  useEffect(() => {
    if (error && error.graphQLErrors.length > 0) {
      toast({
        kind: "error",
        autoHide: false,
        title: "Loading Currencyaccount accounts",
        description: `${error.graphQLErrors.map((e) => `${e.path}: ${e.message}`).join(", ")}`,
      });
    }
  }, [error]);

  return (
    <div className="w-full">
      <DataTable
        data={accounts}
        columns={columns}
        className="overflow-hidden border"
      />
    </div>
  );
}

const columns: ColumnDef<AdminCurrencycloudAccountFragment>[] = [
  {
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    cell: ({ getValue }) => (
      <Link to={`/currencycloud/${getValue<string>()}`}>
        <div className="font-mono text-primary-interactive text-sm underline">
          {getValue<string>()}
        </div>
      </Link>
    ),
  },
  {
    accessorKey: "accountName",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Account Name" />
    ),
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ getValue }) => (
      <div className="capitalize">{getValue<string>()}</div>
    ),
  },
  {
    accessorKey: "country",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Country" />
    ),
  },
  {
    accessorKey: "legalEntityType",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Entity Type" />
    ),
    cell: ({ getValue }) => (
      <div className="capitalize">{getValue<string>()}</div>
    ),
  },
  {
    id: "businessEntity",
    accessorFn: (row) => row.businessEntity,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Business Entity" />
    ),
    cell: ({ row }) =>
      row.original.businessEntity ? (
        <BusinessEntityColumnCell
          businessEntity={row.original.businessEntity}
        />
      ) : (
        "Not linked"
      ),
  },
  {
    accessorKey: "createdAt",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: ({ getValue }) => <DateUI date={new Date(getValue<string>())} />,
  },
];
